import { doodBlockConfig, propertyValue } from "./dood-block-config";
import { cartPagePresetBlockBackgroundColorProperty } from "./cart-page-preset-block-background.util";
import {
  buildOrderedBlockDescriptor,
  type DescriptorFieldOrderItem,
} from "./descriptor-field-order";
import type { IBlockDescriptor } from "./descriptor";

/** Serializable product row used in CB2 cart preview scenarios. */
export type CartItemListPreviewProduct = {
  id: string;
  name: string;
  images?: {
    thumbnail?: { url: string; width?: number; height?: number };
  };
};

/** Serializable cart line item used in CB2 cart preview scenarios. */
export type CartItemListPreviewCartItem = {
  item: {
    id: number;
    quantity: number;
  };
  product: CartItemListPreviewProduct;
  /** Unit price in euros (same as production `unit_price` after cart reconstruction). */
  unit_price: number;
};

/** Serializable shop header for a reconstructed cart group. */
export type CartItemListPreviewShop = {
  id: string;
  name: string;
  slug: string;
};

/** Serializable reconstructed cart group (one shop + its line items). */
export type CartItemListPreviewCart = {
  shop: CartItemListPreviewShop;
  items: readonly CartItemListPreviewCartItem[];
};

/**
 * Serializable preview VM for `CartItemListBlockComponent`.
 * Kept in content-builder so dashboard + webapp share the same transport shape.
 */
export type CartItemListBlockScenarioContent = {
  /** `undefined` while loading; an array (possibly empty) once loaded. */
  reconstructedCart: readonly CartItemListPreviewCart[] | undefined;
  isOrderValidationInProgress: boolean;
};
/**
 * Legacy CB1 field order for `CartItemListBlock`.
 */
export const CART_ITEM_LIST_LEGACY_FIELD_ORDER = [
  { type: "heading", value: "pages.blocks.cart-item-list.clear-cart-navigation.label" },
  "clear_cart_redirect_link",
  { type: "heading", value: "pages.blocks.cart-item-list.colors.label" },
  "heading_color",
  "text_color",
  "secondary_text_color",
  "product_name_color",
  "product_details_color",
  "quantity_badge_text_color",
  "quantity_badge_background_color",
] as const satisfies readonly DescriptorFieldOrderItem[];

const {
  cartItemListBlockDescriptor: _generatedCartItemListBlockDescriptor,
  cartItemListBlockBlocDefinition,
  cartItemListBlockDashboardLayout,
  cartItemListBlockConfig,
  cartItemListBlockResolveConfig,
  cartItemListBlockProperties,
  cartItemListBlockDefinitionSetupScenarios,
} = doodBlockConfig(
  {
    id: "cart-item-list-block",
    name: "Cart - Item list",
    componentType: "CartItemListBlockComponent",
    category: "cart",
    description: "Cart item list.",
    keywords: ["cart", "items"],
    allowedIn: [
      { bloc: 'CartPageComponent', slot: '*' },
    ],


    sharedFields: {},
  },
  () => ({
    background_color: cartPagePresetBlockBackgroundColorProperty(),
    clear_cart_redirect_link: propertyValue({
      type: "link",
      value: "",
      options: {
        label: "pages.blocks.cart-item-list.clear-cart-redirect-link.label",
        help: "pages.blocks.cart-item-list.clear-cart-redirect-link.help",
        modes: ["page", "external", "default"],
        defaultMode: "default",
      },
    }),
    heading_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-800" },
      options: { label: "pages.blocks.cart-item-list.heading-color.label" },
    }),
    text_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-800" },
      options: { label: "pages.blocks.cart-item-list.text-color.label" },
    }),
    secondary_text_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-500" },
      options: { label: "pages.blocks.cart-item-list.secondary-text-color.label" },
    }),
    product_name_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-800" },
      options: { label: "pages.blocks.cart-item-list.product-name-color.label" },
    }),
    product_details_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-600" },
      options: { label: "pages.blocks.cart-item-list.product-details-color.label" },
    }),
    quantity_badge_text_color: propertyValue({
      type: "color",
      value: { type: "hex", value: "#FFFFFF" },
      options: { label: "pages.blocks.cart-item-list.quantity-badge-text-color.label" },
    }),
    quantity_badge_background_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "primary-600" },
      options: { label: "pages.blocks.cart-item-list.quantity-badge-background-color.label" },
    }),
  }),
  () => ({
    tabs: [
      {
        label: "pages.blocks.cart-item-list.tab.content.label",
        fieldsets: [
          {
            legend: "pages.blocks.cart-page.fieldset.appearance.label",
            fields: ["background_color"],
          },
          {
            legend: "pages.blocks.cart-item-list.fieldset.clear-cart.label",
            fields: ["clear_cart_redirect_link"],
          },
          {
            legend: "pages.blocks.cart-item-list.fieldset.title.label",
            fields: ["heading_color"],
          },
          {
            legend: "pages.blocks.cart-item-list.fieldset.shop.label",
            fields: ["secondary_text_color"],
          },
          {
            legend: "pages.blocks.cart-item-list.fieldset.products.label",
            fields: [
              "product_name_color",
              "product_details_color",
              "text_color",
            ],
          },
          {
            legend: "pages.blocks.cart-item-list.fieldset.quantity-badge.label",
            fields: ["quantity_badge_text_color", "quantity_badge_background_color"],
          },
        ],
      },
    ],
  })
);

export function cartItemListBlockDescriptor(): IBlockDescriptor;
export function cartItemListBlockDescriptor(
  fieldOrder: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor;
export function cartItemListBlockDescriptor(
  fieldOrder?: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor {
  if (fieldOrder === undefined) {
    return _generatedCartItemListBlockDescriptor;
  }
  return buildOrderedBlockDescriptor(
    _generatedCartItemListBlockDescriptor,
    cartItemListBlockProperties,
    fieldOrder,
  );
}

export {
  cartItemListBlockBlocDefinition,
  cartItemListBlockDashboardLayout,
  cartItemListBlockConfig,
  cartItemListBlockResolveConfig,
  cartItemListBlockProperties,
  cartItemListBlockDefinitionSetupScenarios,
};

export type CartItemListBlockConfig = typeof cartItemListBlockConfig;
